import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getItem,
} from "../../api/generics";
import AccountingAccount, {
  newAccountingAccount,
} from "../../models/accounting-accounts";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert, warningAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import SingleAccountingAccountSelect from "./SingleAccountingAccountSelect";

interface AccountingAccountFormProps {
  initialAccountingAccount?: AccountingAccount;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const AccountingAccountForm: React.FC<AccountingAccountFormProps> = ({
  initialAccountingAccount,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );
  const [editingAccountingAccount, setEditingAccountingAccount] =
    useState<AccountingAccount>(
      initialAccountingAccount
        ? initialAccountingAccount
        : newAccountingAccount()
    );
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [selectedParentAccountingAccount, setSelectedParentAccountingAccount] =
    useState<AccountingAccount | undefined>(undefined);

  const calculateCodeIdentifier = (
    parentCode: string | undefined,
    codeEnding: string | undefined
  ): string | undefined => {
    if (parentCode === undefined) {
      return codeEnding;
    }
    if (codeEnding === undefined) {
      return parentCode + ".";
    }
    return parentCode + "." + codeEnding;
  };

  const getNextCodeEnding = (lastCodeEnding: string | undefined): string => {
    if (!lastCodeEnding) {
      return "01";
    }

    // Intentar convertir a número y sumar 1
    const lastNumber = parseInt(lastCodeEnding, 10);
    if (!isNaN(lastNumber)) {
      const nextNumber = lastNumber + 1;
      // Si el número tiene un solo dígito, agregar 0 a la izquierda
      return nextNumber < 10 ? `0${nextNumber}` : nextNumber.toString();
    }

    // Si no es un número, devolver "01"
    return "01";
  };

  const fetchLastChild = async (parentId: number) => {
    try {
      const lastChildStatus = await getItem<AccountingAccount>(
        `/accounting_accounts/last_child/${parentId}/`
      );

      if (
        lastChildStatus.status === SUCCESS &&
        lastChildStatus.data !== undefined
      ) {
        // Si existe un último hijo, calcular el siguiente código
        const nextCodeEnding = getNextCodeEnding(
          lastChildStatus.data.codeEnding
        );
        return nextCodeEnding;
      }
    } catch (error) {
      // Si no hay último hijo (404) o error, devolver "1"
      console.log("No se encontró último hijo, iniciando con 01");
    }

    return "01";
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingAccountingAccount({
      ...editingAccountingAccount,
      name: e.target.value,
    });
  };

  const onCodeEndingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCodeIdentifier = calculateCodeIdentifier(
      selectedParentAccountingAccount?.codeIdentifier,
      e.target.value
    );
    setEditingAccountingAccount({
      ...editingAccountingAccount,
      codeEnding: e.target.value,
      codeIdentifier: newCodeIdentifier,
    });
  };

  const onAccountTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value !== "" ? e.target.value : undefined;
    setEditingAccountingAccount({
      ...editingAccountingAccount,
      accountType: newValue,
    });
  };

  const onParentAccountChange = async (
    parentAccount: AccountingAccount | null
  ) => {
    setSelectedParentAccountingAccount(
      parentAccount !== null ? parentAccount : undefined
    );

    let nextCodeEnding = "";

    // Solo autocompletar si estamos creando una nueva cuenta (sin ID)
    if (parentAccount !== null && editingAccountingAccount.id === undefined) {
      nextCodeEnding = await fetchLastChild(parentAccount.id!);
    }
    console.log("Next code ending:", nextCodeEnding);
    const newCodeIdentifier = calculateCodeIdentifier(
      parentAccount?.codeIdentifier,
      nextCodeEnding || undefined
    );

    setEditingAccountingAccount({
      ...editingAccountingAccount,
      codeEnding: nextCodeEnding || editingAccountingAccount.codeEnding,
      codeIdentifier: newCodeIdentifier,
      parentId: parentAccount !== null ? parentAccount.id : undefined,
      accountType:
        parentAccount !== null ? parentAccount.accountType : undefined,
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    let toSendAccountingAccount = {
      ...editingAccountingAccount,
      taxPayerId: taxPayer.id,
    };
    let requestPromise: Promise<ItemRequestStatus<AccountingAccount>>;
    if (editingAccountingAccount.id === undefined) {
      requestPromise = createItem<AccountingAccount>(
        "/accounting_accounts/create/",
        toSendAccountingAccount
      );
    } else {
      requestPromise = updateItem<AccountingAccount>(
        `/accounting_accounts/${toSendAccountingAccount.id}/`,
        toSendAccountingAccount
      );
    }

    const accountingaccountStatus = await requestPromise;

    if (accountingaccountStatus.status !== SUCCESS) {
      if (accountingaccountStatus.errors !== undefined) {
        setErrors(accountingaccountStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (accountingaccountStatus.detail !== undefined) {
        message = accountingaccountStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingAccountingAccount(newAccountingAccount());
    setSelectedParentAccountingAccount(undefined);
  };

  const fetchParent = async (id: number) => {
    const categoriesStatus = await getItem<AccountingAccount>(
      `/accounting_accounts/${id}/`
    );
    if (categoriesStatus.status === SUCCESS) {
      if (categoriesStatus.data !== undefined) {
        setSelectedParentAccountingAccount(categoriesStatus.data);
      }
    } else {
      const message = categoriesStatus.detail
        ? categoriesStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    setEditingAccountingAccount(
      initialAccountingAccount
        ? initialAccountingAccount
        : newAccountingAccount()
    );
    if (initialAccountingAccount?.parentId) {
      fetchParent(initialAccountingAccount.parentId);
    } else {
      setSelectedParentAccountingAccount(undefined);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAccountingAccount]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingAccountingAccount.name)}
                onChange={onNameChange}
                placeholder="Nombre de cuenta contable"
                disabled={editingAccountingAccount.locked || loading}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("name", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Código general en Plan de cuentas:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(
                  editingAccountingAccount.codeIdentifier
                )}
                placeholder="Código de cuenta contable"
                disabled
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("codeIdentifier", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Cuenta Padre:</CLabel>
            </CCol>
            <CCol>
              <SingleAccountingAccountSelect
                value={
                  selectedParentAccountingAccount !== undefined
                    ? selectedParentAccountingAccount
                    : null
                }
                onChange={onParentAccountChange}
                disabled={editingAccountingAccount.locked || loading}
              ></SingleAccountingAccountSelect>
              <FieldErrors
                errors={getFieldErrors("parentId", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Código de cuenta contable:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(
                  editingAccountingAccount.codeEnding
                )}
                onChange={onCodeEndingChange}
                placeholder="Código de cuenta contable"
                disabled={editingAccountingAccount.locked || loading}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("codeEnding", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Tipo de cuenta:</CLabel>
            </CCol>
            <CCol>
              <CSelect
                type="text"
                value={emptyValueOnUndefined(
                  editingAccountingAccount.accountType
                )}
                onChange={onAccountTypeChange}
                disabled={
                  editingAccountingAccount.locked ||
                  loading ||
                  selectedParentAccountingAccount?.accountType !== undefined
                }
              >
                <option value={""}>-----</option>
                <option value={"active"}>Activa/pasiva (Balance)</option>
                <option value={"results"}>
                  Resultados (Estado de Resultados)
                </option>
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("accountType", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton
              type="submit"
              color="primary"
              onClick={onSave}
              disabled={
                loading || editingAccountingAccount.locked || submitting
              }
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default AccountingAccountForm;
